import React from 'react';
import { motion } from 'framer-motion';
import { Dna, ShieldCheck, ThermometerSnowflake } from 'lucide-react';
import { useContent } from '../context/ContentContext';

const CHAPTER_ICONS = [ShieldCheck, Dna, ThermometerSnowflake];

export const MANIFESTO_DEFAULTS = {
  eyebrow: 'Le manifeste du laboratoire',
  title1: 'Trois protocoles.',
  title2: 'Zéro compromis.',
  chapters: [
    {
      num: '01',
      title: 'STÉRILITÉ ABSOLUE',
      text: "Chaque substrat traverse 2 heures d'autoclave à 121 °C. Chaque seringue est remplie sous flux laminaire en salle ISO-5. La contamination n'est pas un risque — c'est une variable éliminée.",
      metric: '99,7 % de protocoles sans contamination',
    },
    {
      num: '02',
      title: 'GÉNÉTIQUE SÉLECTIONNÉE',
      text: "Nos souches sont isolées sur agar, clonées des spécimens les plus vigoureux et stabilisées sur trois générations. Ce que vous inoculez n'est pas une loterie : c'est un phénotype documenté.",
      metric: 'F3 — trois générations de stabilisation',
    },
    {
      num: '03',
      title: 'CULTURE DE PRÉCISION',
      text: "Température, hygrométrie, échanges gazeux : nous publions les paramètres exacts de chaque variété. Vous ne cultivez plus à l'instinct, vous pilotez un écosystème.",
      metric: '±0,3 °C de tolérance sur nos fiches techniques',
    },
  ],
};

const Manifesto = () => {
  const c = useContent('manifesto', MANIFESTO_DEFAULTS);
  const chapters = c.chapters.map((ch, i) => ({ ...ch, icon: CHAPTER_ICONS[i % 3] }));
  return (
  <section id="manifeste" data-testid="manifesto-section" className="relative mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8 lg:py-32">
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, margin: '-80px' }}
      className="mb-4 flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.35em] text-cyber"
    >
      <span className="inline-block h-px w-10 bg-cyber/60" /> {c.eyebrow}
    </motion.p>
    <motion.h2
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.7 }}
      className="font-display text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl lg:text-4xl"
    >
      {c.title1} <span className="text-neon text-glow">{c.title2}</span>
    </motion.h2>

    <div className="mt-16 space-y-0 border-t border-white/5">
      {chapters.map((c, i) => (
        <motion.div
          key={c.num}
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.7, delay: i * 0.1 }}
          className="group relative grid gap-6 border-b border-white/5 py-10 transition-colors hover:bg-panel/40 md:grid-cols-12 md:items-center md:px-6"
          data-testid={`manifesto-chapter-${c.num}`}
        >
          <div className="absolute left-0 top-0 h-0.5 w-0 bg-neon transition-all duration-700 group-hover:w-full" />
          <div className="font-display text-5xl font-extrabold text-white/10 transition-colors group-hover:text-neon/40 md:col-span-2 lg:text-6xl">
            {c.num}
          </div>
          <div className="md:col-span-4">
            <div className="mb-2 flex items-center gap-3">
              <c.icon size={20} className="text-neon" strokeWidth={1.6} />
              <h3 className="font-display text-xl font-bold tracking-tight text-slate-100">{c.title}</h3>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyber/80">{c.metric}</p>
          </div>
          <p className="text-sm leading-relaxed text-slate-400 md:col-span-6">{c.text}</p>
        </motion.div>
      ))}
    </div>
  </section>
  );
};

export default Manifesto;
